import { Capacitor } from "@capacitor/core";
import { readonly, type Ref, ref, watch } from "vue";

/**
 * Shared soft-keyboard state for native (Capacitor) builds. Listeners are
 * registered once at module load; refs are shared across all consumers and
 * are 0 when the keyboard is closed or on web.
 *
 * - `keyboardHeight` updates on will-show/hide (start of the animation) —
 *   use for snappy chrome changes that should lead the animation, e.g.
 *   hiding the bottom nav.
 * - `settledKeyboardHeight` updates on did-show/hide (end of the
 *   animation) — use for actions that need the new viewport in place,
 *   e.g. scrolling content into view.
 *
 * iOS overrides the static `KeyboardResize.Native` config to `None` at
 * runtime: Native shrinks the WKWebView frame invisibly to JS, so layout
 * built on `100dvh` can't reflow. With `None` we drive layout via the
 * `--visible-viewport-height` CSS variable below, which
 * `.full-height-dynamic` consumes. Android stays on Native, but also
 * mirrors `--visible-viewport-height` from `window.visualViewport`:
 * `100dvh` tracks the layout viewport, which can transiently over-report
 * the visible height (post-keyboard resize, background resume, system
 * insets) and push bottom-of-column UI — like the chat composer — off
 * screen. The visual viewport always reports the real visible rect.
 */
const keyboardHeight = ref(0);
const settledKeyboardHeight = ref(0);

if (Capacitor.isNativePlatform()) {
    const isIos = Capacitor.getPlatform() === "ios";
    import("@capacitor/keyboard")
        .then(({ Keyboard, KeyboardResize }) => {
            const noop = () => {};
            if (isIos) {
                Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(noop);
                // Hide the iOS input accessory bar (the Prev/Next/Done toolbar
                // that WKWebView injects above the keyboard on form fields).
                Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(noop);
            }
            Keyboard.addListener("keyboardWillShow", (info) => {
                keyboardHeight.value = info.keyboardHeight;
            }).catch(noop);
            Keyboard.addListener("keyboardWillHide", () => {
                keyboardHeight.value = 0;
            }).catch(noop);
            Keyboard.addListener("keyboardDidShow", (info) => {
                settledKeyboardHeight.value = info.keyboardHeight;
            }).catch(noop);
            Keyboard.addListener("keyboardDidHide", () => {
                settledKeyboardHeight.value = 0;
            }).catch(noop);
        })
        .catch(() => {
            // Plugin unavailable — non-critical, keep refs at 0.
        });

    // iOS: mirror keyboardHeight to a CSS var so layout can shrink.
    // (Not used on Android — its native resize already shrinks the frame,
    // so `innerHeight - keyboardHeight` would double-subtract there.)
    if (isIos) {
        watch(keyboardHeight, (height) => {
            if (height > 0) {
                document.body.style.setProperty("--visible-viewport-height", `${window.innerHeight - height}px`);
            } else {
                document.body.style.removeProperty("--visible-viewport-height");
            }
        });
    } else {
        // Android: keep layout bound to the *visual* viewport. `100dvh`
        // tracks the layout viewport, which can transiently report a
        // height larger than what's visible (e.g. after the keyboard
        // closes or on resume), pushing the chat composer's bottom-of-
        // column content off screen (BUG-1212). `visualViewport.height`
        // is the real visible rect — it already accounts for the native
        // keyboard resize, so this never double-shrinks.
        const syncVisibleViewportHeight = () => {
            const height = window.visualViewport?.height;
            if (height) {
                document.body.style.setProperty("--visible-viewport-height", `${height}px`);
            }
        };
        syncVisibleViewportHeight();
        window.visualViewport?.addEventListener("resize", syncVisibleViewportHeight);
        window.addEventListener("resize", syncVisibleViewportHeight);
    }
}

export function useKeyboard(): {
    keyboardHeight: Readonly<Ref<number>>;
    settledKeyboardHeight: Readonly<Ref<number>>;
} {
    return {
        keyboardHeight: readonly(keyboardHeight),
        settledKeyboardHeight: readonly(settledKeyboardHeight),
    };
}
